import { Big } from 'big.js';
import { PositionStatusReason, PositionStatusReasonPositionLiquidated } from '@/api/spec';
import { Side } from '@/common/constants';
import { stringToBig } from './bigUtils';

/** Proportional split of a liquidated position's remaining equity (fractions sum to 1). */
export interface LiquidationSplit {
  user: number;
  insuranceFund: number;
  exchange: number;
}

export const LIQUIDATION_SPLIT_EVEN: LiquidationSplit = { user: 1 / 3, insuranceFund: 1 / 3, exchange: 1 / 3 };
export const LIQUIDATION_SPLIT_WEIGHTED: LiquidationSplit = { user: 0.8, insuranceFund: 0.1, exchange: 0.1 };

/** Default UTC cutoff when app config doesn't ship one. Sat Jun 20 2026 00:00 UTC. */
export const LIQUIDATION_SPLIT_CHANGE_MS = Date.UTC(2026, 5, 20);

/**
 * Resolves the config cutoff to epoch ms (UTC). Accepts a Unix epoch — seconds, as the backend
 * ships it (e.g. "1781817842"), or milliseconds — or an ISO-8601 string. Falls back to the default
 * for missing/invalid input.
 */
export function resolveLiquidationSplitChangeMs(value?: string): number {
  if (value && /^\d+$/.test(value)) {
    const n = Number(value);
    return n < 1e12 ? n * 1000 : n; // 10-digit epoch is seconds; 13-digit is already ms
  }
  const ms = value ? Date.parse(value) : Number.NaN;
  return Number.isNaN(ms) ? LIQUIDATION_SPLIT_CHANGE_MS : ms;
}

/**
 * Split active for a liquidation event timestamp (epoch ms, UTC — matches `Position.at.t`).
 * Liquidations before `changeMs` split evenly (1/3 each); on/after, 80/10/10.
 */
export function getLiquidationSplit(
  eventTimestampMs: number,
  changeMs: number = LIQUIDATION_SPLIT_CHANGE_MS
): LiquidationSplit {
  return eventTimestampMs < changeMs ? LIQUIDATION_SPLIT_EVEN : LIQUIDATION_SPLIT_WEIGHTED;
}

/** True when a position-history row's status reason marks it as a liquidation. */
export function isLiquidationReason(reason: PositionStatusReason | undefined): boolean {
  return reason === PositionStatusReasonPositionLiquidated;
}

/** Buffers below this fraction render in warning color ("risky"). */
export const LIQUIDATION_BUFFER_WARNING_THRESHOLD = 0.02;
/** Buffers above this fraction render in success color. */
export const LIQUIDATION_BUFFER_SAFE_THRESHOLD = 0.1;

/** Returns the MUI palette path for a fractional buffer; `undefined` means use the inherited text color. */
export function getBufferColor(buffer: number): 'warning.main' | 'success.main' | undefined {
  if (buffer < LIQUIDATION_BUFFER_WARNING_THRESHOLD) return 'warning.main';
  if (buffer > LIQUIDATION_BUFFER_SAFE_THRESHOLD) return 'success.main';
  return undefined;
}

export interface LiquidationCalculationParams {
  entryPrice: number | string | Big;
  collateral: number | string | Big;
  size: number | string | Big;
  side: Side;
  maintenanceMarginFraction: number;
  funding?: number | string | Big;
}

export interface OrderLiquidationParams {
  entryPrice: number | string | Big;
  leverage: number | string | Big;
  side: Side;
  maintenanceMarginFraction: number;
}

export function calculateMaintenanceMarginRequirement(
  entryPrice: number | string | Big,
  size: number | string | Big,
  maintenanceMarginFraction: number
): Big {
  const entryValue = stringToBig(entryPrice.toString()).mul(stringToBig(size.toString()));
  return entryValue.div(maintenanceMarginFraction);
}

function calculateLiquidationPriceCore(
  entryPriceBig: Big,
  collateralBig: Big,
  sizeBig: Big,
  side: Side,
  maintenanceMarginFraction: number,
  fundingBig: Big
): Big {
  if (sizeBig.lte(0) || collateralBig.lte(0)) return new Big(0);

  const maintenanceMarginRequirement = calculateMaintenanceMarginRequirement(entryPriceBig, sizeBig, maintenanceMarginFraction);

  const totalLossForLiquidationBig = maintenanceMarginRequirement.sub(collateralBig).sub(fundingBig);

  const priceDeltaBig = totalLossForLiquidationBig.div(sizeBig);

  const liquidationPrice = side === Side.long ? entryPriceBig.add(priceDeltaBig) : entryPriceBig.sub(priceDeltaBig);

  return liquidationPrice.lt(new Big(0)) ? new Big(0) : liquidationPrice;
}

export const calculateLiquidationPrice = ({
  entryPrice,
  collateral,
  size,
  side,
  maintenanceMarginFraction,
  funding = 0,
}: LiquidationCalculationParams): number => {
  const entryPriceBig = stringToBig(entryPrice.toString());
  const collateralBig = stringToBig(collateral.toString());
  const sizeBig = stringToBig(size.toString());
  const fundingBig = stringToBig(funding.toString());

  return calculateLiquidationPriceCore(
    entryPriceBig,
    collateralBig,
    sizeBig,
    side,
    maintenanceMarginFraction,
    fundingBig
  ).toNumber();
};

/**
 * Fractional adverse move in the mark price needed to reach liquidation. 1 means safe / no
 * liquidation in sight; values <= 0 mean the position is at or past liquidation.
 *
 * Both sides normalize by the current mark — the price the move starts from — so the result is
 * a true "% price move to liquidation" that is symmetric across side (a long and a short the
 * same price-distance from liq read the same buffer) and directly comparable to the stress-test
 * thresholds in {@link sumStressLoss} (e.g. 0.05 = "a 5% adverse move").
 */
export function computeLiquidationBuffer(p: { side: Side; markPrice: number; liquidationPrice: number }): number {
  if (p.liquidationPrice <= 0 || p.markPrice <= 0) return 1;

  const gap = p.side === Side.long ? p.markPrice - p.liquidationPrice : p.liquidationPrice - p.markPrice;
  return Math.max(gap, 0) / p.markPrice;
}

/**
 * Fractional adverse price move from entry to liquidation for an order at the
 * given leverage under isolated margin. Side-independent: equals
 * 1/leverage − 1/maintenanceMarginFraction (both stored as inverse fractions).
 */
export const calculateOrderLiquidationDistance = ({
  leverage,
  maintenanceMarginFraction,
}: {
  leverage: number | string | Big;
  maintenanceMarginFraction: number;
}): number => {
  const leverageBig = stringToBig(leverage.toString());
  if (leverageBig.lte(0) || maintenanceMarginFraction <= 0) return 0;

  const adverseMove = new Big(1).div(leverageBig).sub(new Big(1).div(maintenanceMarginFraction));
  return Math.max(adverseMove.toNumber(), 0);
};

export const calculateOrderLiquidationPrice = ({
  entryPrice,
  leverage,
  side,
  maintenanceMarginFraction,
}: OrderLiquidationParams): Big => {
  const entryPriceBig = stringToBig(entryPrice.toString());
  const leverageBig = stringToBig(leverage.toString());

  // For order calculations, we need to derive effective collateral and size
  // Assume a unit size of 1 for calculation purposes
  const unitSize = new Big(1);
  const positionValue = unitSize.mul(entryPriceBig);
  const effectiveCollateral = positionValue.div(leverageBig);

  return calculateLiquidationPriceCore(
    entryPriceBig,
    effectiveCollateral,
    unitSize,
    side,
    maintenanceMarginFraction,
    new Big(0)
  );
};
